"use client"

import * as React from "react"
import { useLocation } from "react-router-dom"

import { BrokerAccountSwitcher } from "@/components/broker-account-switcher"
import { DashboardIcon } from "@/components/Icons/Icons"
import { NavAccountSettings } from "@/components/nav-account-settings"
import { NavNotificationsCentre } from "@/components/nav-notifications-centre"
import { NavMain } from "@/components/nav-main"
import { isAccountSettingsPathname } from "@/constants/accountSettingsSidebar"
import {
  isNotificationsCentrePathname,
} from "@/constants/notificationsCentre"
import { NavUser } from "@/components/nav-user"
import { SidebarResyncButton } from "@/components/sidebar-resync-button"
import { SidebarSupportButtons } from "@/components/sidebar-support-buttons"
import { BrainIcon } from "@/components/ui/brain"
import { FlaskConicalIcon } from "@/components/ui/flask-conical"
import { ChartSplineIcon } from "@/components/ui/chart-spline"
import { FileChartLineIcon } from "@/components/ui/file-chart-line"
import { GalleryVerticalEndIcon } from "@/components/ui/gallery-vertical-end"
import { LayoutPanelTopIcon } from "@/components/ui/layout-panel-top-icon"
import { LayersIcon } from "@/components/ui/layers"
import { PlayIcon } from "@/components/ui/play"
import { RadioIcon } from "@/components/ui/radio"
import { SettingsMotionIcon } from "@/components/ui/settings-motion"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import { useFeatureFlags } from "@/hooks/useFeatureFlags"
import { isWorkspaceEnabledForUser, shouldHideLegacyPipelineNav } from "@/utils/workspaceFeatureFlag"

const LEGACY_PIPELINE_PATHS = new Set(["/reports", "/simulator", "/strategies"]);

// Three groups split by full-width dividers (no headings): your trading (Dashboard, Trade Log,
// Statistics), building and running strategies (Workspace, Strategy Lab, Launchpad), then University
// and Admin. Reports / Simulator / Strategies are legacy pages (Workspace replaces them for
// non-admins). Tradar is retired.
const baseNavMain = [
    { title: "Dashboard", url: "/dashboard", icon: <DashboardIcon color="inherit" />, group: "trading" },
    { title: "Trade Log", url: "/trade-log", icon: <GalleryVerticalEndIcon color="inherit" />, group: "trading" },
    { title: "Statistics", url: "/statistics", icon: <ChartSplineIcon color="inherit" />, group: "trading" },
    { title: "Workspace", url: "/workspace", icon: <LayoutPanelTopIcon size={22} />, group: "strategies" },
    { title: "Reports", url: "/reports", icon: <FileChartLineIcon color="inherit" />, group: "strategies" },
    { title: "Simulator", url: "/simulator", icon: <PlayIcon color="inherit" />, group: "strategies" },
    { title: "Strategies", url: "/strategies", icon: <LayersIcon color="inherit" />, group: "strategies" },
    { title: "Strategy Lab", url: "/strategy-rd", icon: <FlaskConicalIcon color="inherit" />, group: "strategies" },
    { title: "Launchpad", url: "/launchpad", icon: <RadioIcon color="inherit" />, group: "strategies" },
    { title: "University", url: "/university", icon: <BrainIcon color="inherit" />, group: "more" },
    { title: "Admin", url: "/system", icon: <SettingsMotionIcon color="inherit" />, group: "more" },
]

/**
 * Shadcn app sidebar (`DashboardShell` → `AppSidebar`; sheet on mobile, rail + inset at md+).
 * Legacy Chakra rail is `Sidebar` / `SidebarContent`, not this file.
 */
export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { pathname } = useLocation()
  const { isMobile } = useSidebar()
  const { isAdmin } = useFeatureFlags()
  const accountSettingsView = isAccountSettingsPathname(pathname)
  const notificationsCentreView = isNotificationsCentrePathname(pathname)

  if (notificationsCentreView) {
    return <NavNotificationsCentre {...props} />
  }
  const navMain = React.useMemo(
    () =>
      baseNavMain.filter((item) => {
        // Strategy Lab is on every plan (limited on Free); Launchpad shows its holding screen on Free.
        if (item.url === "/system" && !isAdmin) {
          return false
        }
        if (item.url === "/workspace" && !isWorkspaceEnabledForUser(isAdmin)) {
          return false
        }
        if (shouldHideLegacyPipelineNav(isAdmin) && LEGACY_PIPELINE_PATHS.has(item.url)) {
          return false
        }
        return true
      }),
    [isAdmin]
  )

  return (
    <Sidebar data-testid="shadcn-app-sidebar" {...props}>
      <SidebarHeader>
        <div
          className={cn(
            "flex w-full min-h-0 items-center",
            "group-data-[state=expanded]:h-12",
            "group-data-[state=collapsed]:h-8"
          )}
        >
          <BrokerAccountSwitcher />
        </div>
      </SidebarHeader>
      <SidebarContent>
        {accountSettingsView ? (
          <NavAccountSettings />
        ) : (
          <NavMain items={navMain} />
        )}
      </SidebarContent>
      <SidebarFooter className="gap-2">
        <SidebarSupportButtons />
        <SidebarResyncButton />
        <NavUser />
      </SidebarFooter>
      {!isMobile ? <SidebarRail /> : null}
    </Sidebar>
  )
}
